import React from 'react'
import dynamic from 'next/dynamic'
import { Insight, InsightComponentProps } from 'src/components/insights/types'
import { FullScreenLoading } from 'src/components/full-screen-loading'
import { ShoppingCenterIcon } from 'src/components/insights/icons/shopping-center'
import { Colors } from 'src/components/theme'
import InsightLocked from 'src/components/insight-locked'
import { useTier } from 'src/hooks/use-tier'
import { INSIGHTS_TIER } from 'src/constants/insights-tier'
import { COMMANDAI_IDS_MAP } from 'src/constants/command-ai-ids'

import shoppingCenterOverview1 from 'src/assets/images/insights-previews/shopping-center-overview/shopping_center_overview_1.gif'
import shoppingCenterOverview1Placeholder from 'src/assets/images/insights-previews/shopping-center-overview/shopping_center_overview_1.png'
import shoppingCenterOverview2 from 'src/assets/images/insights-previews/shopping-center-overview/shopping_center_overview_2.gif'
import shoppingCenterOverview2Placeholder from 'src/assets/images/insights-previews/shopping-center-overview/shopping_center_overview_2.png'
import shoppingCenterOverview3 from 'src/assets/images/insights-previews/shopping-center-overview/shopping_center_overview_3.gif'
import shoppingCenterOverview3Placeholder from 'src/assets/images/insights-previews/shopping-center-overview/shopping_center_overview_3.png'
import { InsightContainer } from 'src/v2/views/owner-reveal/insights/container'

const TenantOverviewContent = dynamic(() => import('src/components/insights/tenant-overview/content'), {
  loading: () => <FullScreenLoading />,
  ssr: false,
})

const TenantOverviewPage: React.FC<InsightComponentProps> = (props) => {
  const { insight } = props
  const meetsTier = useTier(INSIGHTS_TIER['Shopping Center Overview'])

  return (
    <InsightContainer insight={insight} color={Colors.purpleDark}>
      <React.Suspense fallback={<FullScreenLoading />}>
        {!meetsTier ? <InsightLocked insight={insight} /> : <TenantOverviewContent insight={insight} />}
      </React.Suspense>
    </InsightContainer>
  )
}

export const TenantOverview: Insight = {
  id: 'tenant-overview',
  component: TenantOverviewPage,
  name: 'Shopping Center Overview',
  icon: ShoppingCenterIcon,
  supportedResultTypes: ['alphamap-location'],
  linkTooltipFile: 'insights-tenant-overview',
  tabId: COMMANDAI_IDS_MAP.mapExplorer['shopping-center-overview-tab'],
  tier: INSIGHTS_TIER['Shopping Center Overview'],
  paywall: {
    title: 'See Shopping Center Overview Reports in Action',
    description: `Shopping Center isn't available on your current plan. However, you can view full functionality of this feature by viewing an unlocked location or upgrading your plan below.`,
    synopsis: [
      {
        image: shoppingCenterOverview1.src,
        imagePlaceholder: shoppingCenterOverview1Placeholder.src,
        title: 'Unlock Retail Intelligence ',
        description: 'Easily understand tenant composition and center dynamics for smart retail insights.',
      },
      {
        image: shoppingCenterOverview2.src,
        imagePlaceholder: shoppingCenterOverview2Placeholder.src,
        title: 'Smarter Leasing Decisions',
        description: 'Make informed leasing choices with data on tenant performance and center trends.',
      },
      {
        image: shoppingCenterOverview3.src,
        imagePlaceholder: shoppingCenterOverview3Placeholder.src,
        title: 'Tailored Marketing Strategies',
        description: 'Use foot traffic insights for effective, customer-focused marketing strategies.',
      },
    ],
  },
}
